import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Banknote, FileText, Send } from "lucide-react";

const eur = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

function IncassoForm({ t, onDone }) {
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({ data: new Date().toISOString().slice(0, 10), importo: "", modalita: "bonifico", compagnia: "", numero_polizza: "", sede: "", produttore: "", note: "" });
  useEffect(() => { api.get("/trattative/incasso/meta").then(({ data }) => { setMeta(data); setF((x) => ({ ...x, sede: data.sedi[0] })); }); }, []);
  if (!meta) return null;
  const mod = Object.entries(meta.modalita).filter(([k]) => t.canale !== "distanza" || meta.modalita_distanza.includes(k));
  const salva = async () => { try { const { data } = await api.post(`/trattative/${t.id}/incasso`, { ...f, importo: Number(String(f.importo).replace(",", ".")) }); onDone(data); toast.success("Incasso registrato in Prima Nota"); } catch (e) { err(e); } };
  const sel = (k, opts) => (
    <select value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-8 w-full text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid={`inc-${k}`}>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
  );
  const inp = (k, label, type = "text") => (
    <div className="space-y-1"><Label className="text-[11px]">{label}</Label><Input type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-8 text-sm" data-testid={`inc-${k}`} /></div>
  );
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {inp("data", "Data del pagamento", "date")}{inp("importo", "Importo (€)")}
        <div className="space-y-1"><Label className="text-[11px]">Modalità</Label>{sel("modalita", mod)}</div>
        {inp("compagnia", "Compagnia")}{inp("numero_polizza", "Numero polizza")}
        <div className="space-y-1"><Label className="text-[11px]">Sede</Label>{sel("sede", meta.sedi.map((s) => [s, s]))}</div>
      </div>
      {t.canale === "distanza" && <p className="text-[11px] text-slate-500">Vendita a distanza: contanti e assegno non ammessi.</p>}
      <Button size="sm" onClick={salva} disabled={!f.importo || !f.compagnia || !f.numero_polizza} className="brand-gradient text-white border-0" data-testid="inc-salva">Registra incasso</Button>
    </div>
  );
}

export function IncassoBox({ t, onDone }) {
  const inc = t.incasso;
  const passo = (t.passi || []).find((p) => p.key === "pagamento");
  const invia = async () => {
    if (!window.confirm("Ha verificato la quietanza? Verrà inviata al cliente e pubblicata nella sua pagina personale.")) return;
    try { const { data } = await api.post(`/trattative/${t.id}/incasso/quietanza/invia`); onDone(data); toast.success("Quietanza inviata al cliente"); } catch (e) { err(e); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3" data-testid="trt-incasso-box">
      <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Banknote className="h-4 w-4 text-teal-700" /> Incasso del premio</h3>
      {inc ? (
        <div className="space-y-2 text-sm" data-testid="inc-registrato">
          <p className="text-slate-700"><b>{eur(inc.importo)}</b> · {inc.modalita_label} · {new Date(inc.data).toLocaleDateString("it-IT")} · {inc.compagnia} polizza {inc.numero_polizza}
            <span className="block text-xs text-slate-500">Registrato in Prima Nota da {inc.by} (movimento {inc.movimento_id})</span></p>
          <div className="flex gap-2 flex-wrap items-center">
            <Button asChild size="sm" variant="outline"><a href={`${API}/trattative/${t.id}/incasso/quietanza`} target="_blank" rel="noreferrer" data-testid="inc-quietanza"><FileText className="h-3.5 w-3.5 mr-1" /> Verifica quietanza</a></Button>
            {inc.quietanza_inviata_at ? <span className="text-xs text-emerald-700" data-testid="inc-quietanza-inviata">Quietanza inviata il {new Date(inc.quietanza_inviata_at).toLocaleString("it-IT")}</span>
              : <Button size="sm" onClick={invia} data-testid="inc-quietanza-invia"><Send className="h-3.5 w-3.5 mr-1" /> Verificata: invia al cliente</Button>}
          </div>
        </div>
      ) : passo?.bloccato ? <p className="text-sm text-slate-500" data-testid="inc-bloccato">Disponibile dopo la firma del cliente (percorso obbligato).</p>
        : <IncassoForm t={t} onDone={onDone} />}
    </div>
  );
}
